<template>
    <el-container style="height: calc(100vh - 80px)" class="bg-white">
        <el-header style="padding: 0px; height: auto; ">
            <!-- title部 -->
            <div class="h-12 p-4 shadow-md">詳細情報</div>

            <!-- 作業情报 -->
            <div class="flex flex-col md:flex-row md:flex-wrap gap-x-10 gap-y-4 p-5">
                <div class="flex flex-col">
                    <div>
                        <span class="font-bold">作業員名：</span>{{ operatorName }}
                        <span class="font-bold ml-4">作業員コード：</span>{{ operatorCode }}
                    </div>
                    <div class="mt-2">
                        <span class="font-bold">現場名：</span>{{ siteName }}
                    </div>
                </div>

                <div class="flex items-center justify-start ">
                    <span class="font-bold">時間範囲：</span>
                    <el-radio-group v-model="timeRange">
                        <el-radio-button :value="4">
                            <div class="text-xs md:text-base">{{ isSmallScreen ? '4h' : '4時間' }}</div>
                            <div v-if="!isSmallScreen" class="text-xs">5分間隔</div>
                        </el-radio-button>
                        <el-radio-button :value="8">
                            <div class="text-xs md:text-base">{{ isSmallScreen ? '8h' : '8時間' }}</div>
                            <div v-if="!isSmallScreen" class="text-xs">10分間隔</div>
                        </el-radio-button>
                        <el-radio-button :value="12">
                            <div class="text-xs md:text-base">{{ isSmallScreen ? '12h' : '12時間' }}</div>
                            <div v-if="!isSmallScreen" class="text-xs">15分間隔</div>
                        </el-radio-button>
                        <el-radio-button :value="24">
                            <div class="text-xs md:text-base">{{ isSmallScreen ? '24h' : '24時間' }}</div>
                            <div v-if="!isSmallScreen" class="text-xs">30分間隔</div>
                        </el-radio-button>
                    </el-radio-group>
                </div>
            </div>
        </el-header>

        <!-- 主显示部 -->
        <el-main style="padding-block: 0px;">
            <div class="flex flex-col md:flex-row gap-4">
                <!-- Chart with overlay buttons -->
                <div class="w-full md:w-3/5" style="height: calc(100vh - 300px);">
                    <div class="mb-2 text-lg font-semibold text-gray-700">履歴グラフ</div>

                    <!-- Chart Component -->
                    <ChartComponent v-loading="chartLoading" :chartData="chartData"
                        :showErrorOverlay="errorStates.chart" @bar-click="handleBarClick"
                        :legends-orient-right="!isSmallScreen" @bars-selected="handleBarsSelected" />
                </div>

                <!-- Map -->
                <div class="w-full md:w-2/5 md:pb-5" style="height: calc(100vh - 300px);">
                    <div class="mb-2 text-lg font-semibold text-gray-700">
                        {{ formatMapTitle() }}
                    </div>
                    <MapComponent v-loading="mapLoading" :mode="mapDisplayMode" :mapNewestData="mapNewestData"
                        :mapTrackingData="mapTrackingData" :showErrorOverlay="errorStates.map" />
                </div>
            </div>

            <!-- 底部按钮 -->
            <div class="flex flex-wrap justify-center items-center pt-4 space-x-6">
                <el-button class="flex items-center" @click="navigateTime('back')" type="primary" text>
                    <span class="material-icons">arrow_back</span>
                    <span class="ml-2">前の{{ timeRange }}時間</span>
                </el-button>

                <span>{{ formatTimeRange }}</span>

                <el-button class="flex items-center" @click="navigateTime('forward')" type="primary" text
                    :disabled="isLatestTimeFrame">
                    <span class="mr-2">次の{{ timeRange }}時間</span>
                    <span class="material-icons">arrow_forward</span>
                </el-button>
            </div>
        </el-main>

        <el-footer style="height: 20px;"> </el-footer>
    </el-container>
</template>

<script lang="ts">
// import axios, { AxiosResponse } from 'axios';
import { checkToken } from '@/components/common/auth';
import { defineComponent, onMounted, ref, computed, watch, onUnmounted } from 'vue';
import { useRoute } from 'vue-router'
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import 'material-icons/iconfont/material-icons.css';
import ChartComponent from './chart-component.vue';
import MapComponent from './map-component.vue';
import { VitalChartAggrData } from '../../models/vitalChartAggrData';
import { VitalRecordData } from '../../models/vitalRecordData';
import { ElMessage } from 'element-plus';
import { getErrorMessage } from '@/components/common/errorMessage';

export default defineComponent({
    name: 'OperatorDetail',
    components: {
        ChartComponent,
        MapComponent
    },
    setup() {
        // States
        const loading = ref(true);
        const entryLoading = ref(true);
        const chartData = ref<VitalChartAggrData[]>([]);
        const mapNewestData = ref<VitalRecordData | null>(null);
        const mapTrackingData = ref<any[]>([]);
        const mapDisplayMode = ref<'NewestLocationInTimeRange' | 'TrackInTimeRange'>('NewestLocationInTimeRange');

        const chartContainer = ref<HTMLElement | null>(null);

        const pageIndex = ref(0);  // 0 = current 12 hours, 1 = 12-24 hours ago, etc.
        const isLatestTimeFrame = computed(() => pageIndex.value === 0);

        // Update the formatTimeRange computed property
        const formatTimeRange = computed(() => {
            const now = new Date();
            const endTime = pageIndex.value === 0 ? now : new Date(now.getTime() - (pageIndex.value * timeRange.value * 60 * 60 * 1000));
            const startTime = new Date(endTime.getTime() - (timeRange.value * 60 * 60 * 1000));

            // Format: YYYY年MM月DD日 HH:mm
            const formatDateTime = (date: Date) => {
                return date.toLocaleString('ja-JP', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }).replace(/\//g, '年').replace(/\//g, '月').replace(/ /g, '日 ');
            };

            return `${formatDateTime(startTime)} ～ ${formatDateTime(endTime)}`;
        });

        const fetchAttempted = ref(false);

        const fetchBasicInfo = async () => {
            try {
                const response = await axios.get('/api/operator/detail/basic_info', {
                    params: { operator_code: operatorCode.value, site_code: siteCode.value }
                });

                if (response.data.code === 0) {
                    operatorName.value = response.data.data.operator.operator_name;
                    siteName.value = response.data.data.site.site_name;
                    return true;
                } else {
                    // console.error(response.data.message);
                    return false;
                }
            } catch (error) {
                ElMessage.error(getErrorMessage(error as Error));
                // console.error('Error fetching basic info:', error);
                return false;
            } finally {
                entryLoading.value = false;
            }
        }

        // Separate loading states for map and chart
        const chartLoading = ref(true);
        const mapLoading = ref(true);

        const fetchData = async () => {
            try {
                mapLoading.value = true;
                chartLoading.value = true;

                const [chartResponse, mapResponse] = await Promise.all([
                    axios.get('/api/operator/detail/chart_data', {
                        params: {
                            operator_code: operatorCode.value,
                            site_code: siteCode.value,
                            page_index: pageIndex.value,
                            time_range: timeRange.value
                        }
                    }),
                    axios.get('/api/operator/detail/map_data_by_page', {
                        params: {
                            operator_code: operatorCode.value,
                            site_code: siteCode.value,
                            page_index: pageIndex.value,
                            time_range: timeRange.value
                        }
                    })
                ]);

                fetchAttempted.value = true;

                if (chartResponse.data.code === 0) {
                    chartData.value = chartResponse.data.data;
                } else {
                    chartData.value = [];
                    ElMessage.error(chartResponse.data.message);
                }

                if (mapResponse.data.code === 0) {
                    mapNewestData.value = mapResponse.data.data;
                } else {
                    mapNewestData.value = null;
                    ElMessage.error(mapResponse.data.message);
                }

            } catch (error) {
                // console.error('Error fetching data:', error);
                ElMessage.error(getErrorMessage(error as Error));
            } finally {
                mapLoading.value = false;
                chartLoading.value = false;
                loading.value = false;
            }
        };

        // Navigate time backward or forward
        const navigateTime = (direction: 'back' | 'forward') => {
            if (direction === 'back') {
                // Move back 4 hours (increase page index)
                pageIndex.value++;
            } else if (direction === 'forward' && !isLatestTimeFrame.value) {
                // Move forward 4 hours (decrease page index, but not below 0)
                pageIndex.value = Math.max(0, pageIndex.value - 1);
            }
        };

        // Replace the time watch with a pageIndex watch
        watch(() => pageIndex.value, () => {
            fetchData();
        });

        const route = useRoute();

        // 参数获取
        const siteCode = ref('');
        const operatorCode = ref('');
        const operatorName = ref('');
        const siteName = ref('');

        const timeRange = ref(4);

        const isSmallScreen = ref(window.innerWidth < 768);
        const resizeTimeout = ref<number | null>(null);

        const handleResize = () => {
            isSmallScreen.value = window.innerWidth < 768

            if (resizeTimeout.value) {
                clearTimeout(resizeTimeout.value);
            }

            resizeTimeout.value = setTimeout(() => {
                // Perform resize operations here
                resizeTimeout.value = null;
            }, 200);
        };

        onMounted(async () => {
            checkToken();

            // Get all route params in one go
            const { site_code, operator_code } = route.params;

            siteCode.value = site_code as string || '';
            operatorCode.value = operator_code as string || '';

            if (await fetchBasicInfo()) {
                await fetchData();
            }
            window.addEventListener('resize', handleResize);
            isSmallScreen.value = window.innerWidth < 768
        });

        onUnmounted(() => {
            window.removeEventListener('resize', handleResize);
            if (resizeTimeout.value) {
                clearTimeout(resizeTimeout.value);
            }
        });

        // Computed properties to determine component errors
        const errorStates = computed(() => {
            return {
                chart: fetchAttempted.value && (!chartData.value || chartData.value.length === 0),
                map: fetchAttempted.value && ((mapDisplayMode.value === 'NewestLocationInTimeRange' && !mapNewestData.value) || (mapDisplayMode.value === 'TrackInTimeRange' && (!mapTrackingData.value || mapTrackingData.value.length === 0)))
            };
        });

        const formatMapTitle = (): string => {
            if (mapDisplayMode.value === 'NewestLocationInTimeRange') {
                if (!mapNewestData.value) {
                    return "時間帯に最新データの位置なし";
                } else {
                    const dateString = mapNewestData.value.check_timestamp;

                    // Create a date object from either format
                    const date = new Date(dateString);

                    // Check if date is valid
                    if (isNaN(date.getTime())) {
                        return "時間帯に最新データの位置なし";
                    }

                    // Format directly with date components to ensure reliability
                    const year = date.getFullYear();
                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                    const day = date.getDate().toString().padStart(2, '0');
                    const hours = date.getHours().toString().padStart(2, '0');
                    const minutes = date.getMinutes().toString().padStart(2, '0');

                    // Format: YYYY年MM月DD日HH時MM分の位置情報
                    return `${year}年${month}月${day}日${hours}時${minutes}分の位置情報`;
                }
            } else if (mapDisplayMode.value === 'TrackInTimeRange') {
                if (!mapTrackingData.value || mapTrackingData.value.length === 0) {
                    return "時間帯にの位置なし";
                } else {
                    return "時間帯にの位置";
                }
            } else {
                return "時間帯にの位置なし";
            }


        };

        const handleSingleBarCheck = async (timestamp: number) => {
            mapLoading.value = true; // Only set map loading to true

            try {
                // Fetch map data for the specific timestamp
                const mapResponse = await axios.get('/api/operator/detail/map_data_by_time', {
                    params: {
                        operator_code: operatorCode.value,
                        site_code: siteCode.value,
                        search_time: timestamp,
                        time_range: timeRange.value
                    }
                });

                if (mapResponse.data.code === 0) {
                    // Update map data
                    mapNewestData.value = mapResponse.data.data;
                } else {
                    mapNewestData.value = null;
                    // ElMessage.error(mapResponse.data.message);
                }
            } catch (err) {
                // console.error('Error fetching map data by time:', err);
                ElMessage.error(getErrorMessage(err as Error));
                mapNewestData.value = null;
            } finally {
                mapTrackingData.value = [];
                mapDisplayMode.value = 'NewestLocationInTimeRange';
                mapLoading.value = false; // Only set map loading to false
            }
        };

        const handleMultipleBarCheck = ({ startTimestamp, endTimestamp }: { startTimestamp: number, endTimestamp: number }) => {
            mapLoading.value = true;

            // Format timestamps for API request
            const formattedStartTime = new Date(startTimestamp).toISOString();
            const formattedEndTime = new Date(endTimestamp).toISOString();

            // Call the tracking data API with the selected time range
            axios.get('/api/operator/detail/map_tracking_data', {
                params: {
                    operator_code: operatorCode.value,
                    site_code: siteCode.value,
                    search_start_time: formattedStartTime,
                    search_end_time: formattedEndTime,
                    time_range: timeRange.value
                }
            }).then(response => {
                if (response.data.code === 0) {
                    mapTrackingData.value = response.data.data || [];
                    mapDisplayMode.value = 'TrackInTimeRange';
                } else {
                    mapTrackingData.value = [];
                    // ElMessage.error(response.data.message);
                }
            }).catch(err => {
                // console.error('Error in tracking data request:', err);
                ElMessage.error(getErrorMessage(err as Error));
                mapTrackingData.value = [];
            }).finally(() => {
                mapLoading.value = false;
                mapNewestData.value = null;
            });
        };

        // Also ensure the watch for timeRange clears tracking data
        watch(timeRange, () => {
            mapTrackingData.value = []; // Clear tracking data when time range changes
            fetchData();
        });

        return {
            siteCode,
            operatorCode,
            operatorName,
            siteName,
            loading,
            entryLoading,
            chartContainer,
            formatTimeRange,
            navigateTime,
            isLatestTimeFrame,
            chartData,
            mapNewestData,
            fetchAttempted,
            errorStates,
            formatMapTitle,
            handleBarClick: handleSingleBarCheck,
            chartLoading,
            mapLoading,
            timeRange,
            handleBarsSelected: handleMultipleBarCheck,
            mapTrackingData,
            mapDisplayMode,
            isSmallScreen,
        }

    }
});
</script>

<style>
/* Fix for material icons alignment */
.custom-div-icon {
    background: none;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>

<style scoped>
.z-index-1000 {
    z-index: 1000;
}

.chart-button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 90px;
    opacity: 0.85;
    transition: opacity 0.2s;
}

.chart-button:hover {
    opacity: 1;
}

@media (max-width: 768px) {
    .chart-button {
        min-width: 40px;
        padding: 6px;
    }

    .chart-button span.material-icons {
        margin-right: 0;
    }

    .chart-button span:not(.material-icons) {
        display: none;
    }
}
</style>